// The one place this app talks to the API.
//
// Everything goes through `request()`, so the Authorization header, the error
// shape and the base URL are decided once. A component that reaches for `fetch`
// directly gets none of them, and the first thing it loses is the server's own
// error message.
//
// Token acquisition lives in config/auth.ts (silent first, then an interactive
// redirect) because it is MSAL-shaped. This file calls it and does nothing clever
// with the result.
import { apiUrl } from '../config/api';
import { acquireApiToken, AUTH_BYPASS } from '../config/auth';
import type { ApiErrorBody } from './types';

/**
 * A failed API call, with everything the caller needs to decide what to do.
 *
 * `message` is the SERVER's sentence when it sent one. That matters more here
 * than in most apps: the API's refusals are written for the person at the line —
 * "You do not hold a current CAPTURE authorisation. Ask a QA manager to grant
 * one." Replacing that with "Request failed" throws away the only actionable
 * sentence on the screen, and the operator is left guessing at a gate that
 * already told them exactly what was wrong.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    /** HTTP status, or 0 when the request never reached the server. */
    readonly status: number,
    /** The API's stable machine code (`reason` in the body), when it sent one.
     *  Branch on this, never on the prose. */
    readonly reason: string | null,
    /** The parsed body, for anything the two fields above do not carry. */
    readonly body: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** The request never left the device, or never got an answer. */
  get isNetwork(): boolean {
    return this.status === 0;
  }

  /** Signed out, token expired, or the action is refused. The offline queue KEEPS
   *  an item that fails this way — the capture is real, and what is wrong is the
   *  session, not the reading. */
  get isAuth(): boolean {
    return this.status === 401 || this.status === 403;
  }

  /** Worth sending again later: nothing about the request was wrong. A 4xx other
   *  than auth is not retryable — resending it produces the same refusal. */
  get isRetryable(): boolean {
    return this.status === 0 || this.status >= 500;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal } = options;
  const headers = new Headers();

  if (body !== undefined) headers.set('Content-Type', 'application/json');

  if (!AUTH_BYPASS) {
    try {
      headers.set('Authorization', `Bearer ${await acquireApiToken()}`);
    } catch (err) {
      // Classified 401 rather than allowed to escape as a bare Error, so every
      // caller has one type to catch and the offline queue's "keep on auth
      // failure" rule covers a token that could not be minted as well as one the
      // API rejected. Sending the request without the header instead would turn
      // this into a 401 about a missing header, with the real cause nowhere in it.
      throw new ApiError(
        err instanceof Error ? err.message : 'Could not acquire an access token.',
        401,
        'token_unavailable',
        null,
      );
    }
  }

  let response: Response;
  try {
    response = await fetch(apiUrl(path), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (err) {
    // A thrown fetch is the plant Wi-Fi, not a bug. Status 0 is what tells the
    // offline queue to keep the item and back off rather than discard it.
    throw new ApiError(
      err instanceof Error && err.name === 'AbortError'
        ? 'The request was cancelled.'
        : 'No connection to the Quality API. The reading is kept and will be sent when the network returns.',
      0,
      'network',
      null,
    );
  }

  // Read as text first: an error from the platform rather than the app — a 502
  // from the host, an HTML page from a misrouted request — is not JSON, and
  // `response.json()` on it throws a parse error that hides the real status.
  const text = await response.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!response.ok) {
    const errorBody = (typeof parsed === 'object' && parsed !== null ? parsed : null) as
      | ApiErrorBody
      | null;
    throw new ApiError(
      errorBody?.error ?? `${response.status} ${response.statusText || 'Request failed'}`,
      response.status,
      errorBody?.reason ?? null,
      parsed,
    );
  }

  // 204 and an empty 200 both land here. Cast rather than invent an empty object:
  // a caller typing a void route as `Promise<void>` gets what it asked for.
  return parsed as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { method: 'GET', signal }),
  post: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, { method: 'POST', body, signal }),
  put: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, { method: 'PUT', body, signal }),
  patch: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, { method: 'PATCH', body, signal }),
  delete: <T>(path: string, signal?: AbortSignal) => request<T>(path, { method: 'DELETE', signal }),
  /**
   * A file the API streams (an inspection photo, migration 019), as a Blob. An <img src>
   * cannot carry the bearer token and the container is never public, so the office reads
   * the bytes here and shows them through an object URL. Errors as request() does.
   */
  blob: async (path: string, signal?: AbortSignal): Promise<Blob> => {
    const headers = new Headers();
    if (!AUTH_BYPASS) {
      try {
        headers.set('Authorization', `Bearer ${await acquireApiToken()}`);
      } catch (err) {
        throw new ApiError(err instanceof Error ? err.message : 'Could not acquire an access token.', 401, 'token_unavailable', null);
      }
    }
    let response: Response;
    try {
      response = await fetch(apiUrl(path), { headers, signal });
    } catch {
      throw new ApiError('No connection to the Quality API.', 0, 'network', null);
    }
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as ApiErrorBody | null;
      throw new ApiError(body?.error ?? `${response.status} ${response.statusText || 'Request failed'}`, response.status, body?.reason ?? null, body);
    }
    return response.blob();
  },
};

/** The sentence to show a person for any thrown value. Prefers the server's own
 *  wording; falls back to the error's message; never renders "[object Object]". */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Something went wrong.';
}
